import { useCallback, useEffect, useRef, useState } from "react";

const MAX_HISTORY = 50;

/**
 * Undo/redo state hook. Tracks up to MAX_HISTORY states.
 * Call `set(next)` to push a new state. Call `replace(next)` to
 * overwrite the current state without creating a history entry
 * (useful when rehydrating from the server after save/load).
 */
export function useHistoryState<T>(initial: T) {
  const [past, setPast] = useState<T[]>([]);
  const [present, setPresent] = useState<T>(initial);
  const [future, setFuture] = useState<T[]>([]);

  const presentRef = useRef(present);
  presentRef.current = present;

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved =
        typeof next === "function"
          ? (next as (prev: T) => T)(presentRef.current)
          : next;
      if (resolved === presentRef.current) return;
      setPast((p) => {
        const trimmed = p.length >= MAX_HISTORY ? p.slice(1) : p;
        return [...trimmed, presentRef.current];
      });
      setPresent(resolved);
      setFuture([]);
    },
    []
  );

  const replace = useCallback((next: T) => {
    setPast([]);
    setFuture([]);
    setPresent(next);
  }, []);

  const undo = useCallback(() => {
    setPast((p) => {
      if (p.length === 0) return p;
      const prev = p[p.length - 1];
      setFuture((f) => [presentRef.current, ...f]);
      setPresent(prev);
      return p.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (f.length === 0) return f;
      const next = f[0];
      setPast((p) => [...p, presentRef.current]);
      setPresent(next);
      return f.slice(1);
    });
  }, []);

  return {
    state: present,
    set,
    replace,
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0
  };
}
